import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
    IndianRupee, 
    Check, 
    X, 
    Eye, 
    AlertCircle, 
    Search,
    Calendar,
    CheckCircle2,
    XCircle,
    Clock,
    FileText,
    TrendingUp,
    Briefcase,
    Tag,
    Download,
    ChevronLeft,
    ChevronRight
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf/dist/jspdf.umd.min.js";
import "react-calendar/dist/Calendar.css";
import { toast } from "react-hot-toast";
import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const Expenses = () => {
    const navigate = useNavigate();
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [teamFilter, setTeamFilter] = useState("all");
    const [titleFilter, setTitleFilter] = useState("all");
    const [statusTab, setStatusTab] = useState("Pending"); // Pending, Approved, Rejected
    
    // Rejection Modal
    const [rejectingClaim, setRejectingClaim] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [submittingReject, setSubmittingReject] = useState(false);
    const [processingId, setProcessingId] = useState("");

    // Export Modal
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportType, setExportType] = useState("monthly"); // monthly, yearly, custom
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [exportingPdf, setExportingPdf] = useState(false);

    // Calendar for month selection
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [showMonthCalendar, setShowMonthCalendar] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        document.title = "Expenses | Savrion Technologies";
        setCurrentPage(1);
    }, [searchQuery, teamFilter, titleFilter, statusTab]);

    const fetchClaims = useCallback(async () => {
        try {
            const res = await api.get("/api/admin/expenses");
            setClaims(res.data);
        } catch (err) {
            if ([401, 403].includes(err.response?.status)) navigate("/admin/login");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    const [teams, setTeams] = useState([
        { _id: "software", name: "Software", value: "software" },
        { _id: "embedded", name: "Embedded", value: "embedded" },
        { _id: "designteam", name: "Design Team", value: "designteam" },
        { _id: "sales team", name: "Sales Team", value: "sales team" },
        { _id: "tele marketing", name: "Tele Marketing", value: "tele marketing" },
        { _id: "digital marketing", name: "Digital Marketing", value: "digital marketing" }
    ]);

    useEffect(() => {
        document.title = "Expenses | Savrion Technologies";
        const fetchTeams = async () => {
            try {
                const res = await api.get('/api/teams');
                if (res.data && res.data.length > 0) {
                    const dbTeams = res.data.map(t => ({ _id: t._id, name: t.name, value: t.name }));
                    const defaults = [
                        { _id: "software", name: "Software", value: "software" },
                        { _id: "embedded", name: "Embedded", value: "embedded" },
                        { _id: "designteam", name: "Design Team", value: "designteam" },
                        { _id: "sales team", name: "Sales Team", value: "sales team" },
                        { _id: "tele marketing", name: "Tele Marketing", value: "tele marketing" },
                        { _id: "digital marketing", name: "Digital Marketing", value: "digital marketing" }
                    ];
                    const merged = [...dbTeams];
                    defaults.forEach(def => {
                        if (!merged.some(t => t.name.toLowerCase() === def.name.toLowerCase())) {
                            merged.push(def);
                        }
                    });
                    setTeams(merged);
                }
            } catch (err) {
                console.error("Failed to fetch teams:", err);
            }
        };
        fetchTeams();
        fetchClaims();
    }, [fetchClaims]);

    const handleApprove = async (id) => {
        toast((toastItem) => (
            <div className="flex max-w-sm items-center gap-3">
                <span className="text-sm font-semibold">Approve this expense claim?</span>
                <button
                    type="button"
                    className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-black text-white"
                    onClick={async () => {
                        toast.dismiss(toastItem.id);
                        setProcessingId(id);
                        try {
                            await api.patch(`/api/admin/expenses/${id}/status`, { status: "Approved" });
                            toast.success("Expense claim approved.");
                            fetchClaims();
                        } catch (err) {
                            toast.error(err.response?.data?.message || "Failed to approve claim");
                        } finally {
                            setProcessingId("");
                        }
                    }}
                >
                    Approve
                </button>
            </div>
        ));
    };

    const openRejectModal = (claim) => {
        setRejectingClaim(claim);
        setRejectionReason("");
    };

    const handleRejectSubmit = async (e) => {
        e.preventDefault();
        if (!rejectionReason.trim()) {
            toast.error("Please enter a rejection reason.");
            return;
        }
        setSubmittingReject(true);
        try {
            await api.patch(`/api/admin/expenses/${rejectingClaim._id}/status`, {
                status: "Rejected",
                rejectionReason: rejectionReason
            });
            setRejectingClaim(null);
            fetchClaims();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to reject claim");
        } finally {
            setSubmittingReject(false);
        }
    };

    const viewPdf = (base64Data) => {
        const win = window.open();
        win.document.write(`<iframe src="${base64Data}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`);
    };

    const handleViewReceipt = async (claimId) => {
        try {
            const res = await api.get(`/api/expenses/${claimId}/receipt`);
            if (res.data && res.data.receipt) {
                viewPdf(res.data.receipt);
            } else {
                toast.error("Receipt content not found.");
            }
        } catch (err) {
            console.error("Failed to fetch receipt:", err);
            toast.error("Failed to load receipt.");
        }
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });

    // Export PDF function
    const generatePDF = (expensesToExport, dateRange) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let yPosition = 15;

        // Title
        doc.setFontSize(20);
        doc.setFont(undefined, 'bold');
        doc.text("Expense Report", pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;

        // Date range
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100);
        doc.text(`Report Period: ${dateRange}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 12;

        // Summary section
        const totalAmount = expensesToExport.reduce((sum, c) => sum + c.amount, 0);
        const approvedClaims = expensesToExport.filter(c => c.status === "Approved");
        const approvedAmount = approvedClaims.reduce((sum, c) => sum + c.amount, 0);

        doc.setFont(undefined, 'bold');
        doc.setTextColor(0);
        doc.text("Summary:", 15, yPosition);
        yPosition += 7;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Total Claims: ${expensesToExport.length}`, 20, yPosition);
        yPosition += 5;
        doc.text(`Approved Claims: ${approvedClaims.length}`, 20, yPosition);
        yPosition += 5;
        doc.text(`Total Amount: ₹${totalAmount.toLocaleString('en-IN')}`, 20, yPosition);
        yPosition += 5;
        doc.text(`Approved Amount: ₹${approvedAmount.toLocaleString('en-IN')}`, 20, yPosition);
        yPosition += 12;

        // Table headers
        doc.setFont(undefined, 'bold');
        doc.setFontSize(10);
        doc.setTextColor(40, 40, 40);
        const headers = ['Employee', 'Title', 'Amount', 'Date', 'Status'];
        const columnWidths = [35, 35, 25, 25, 25];
        let xPosition = 15;

        headers.forEach((header, i) => {
            doc.text(header, xPosition, yPosition, { maxWidth: columnWidths[i] });
            xPosition += columnWidths[i];
        });
        yPosition += 8;

        // Table data
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.setTextColor(0);

        expensesToExport.forEach(claim => {
            if (yPosition > pageHeight - 15) {
                doc.addPage();
                yPosition = 15;
            }

            xPosition = 15;
            const cells = [
                claim.employee?.name || 'N/A',
                claim.title || 'N/A',
                `₹${claim.amount.toLocaleString('en-IN')}`,
                formatDate(claim.date),
                claim.status
            ];

            cells.forEach((cell, i) => {
                doc.text(cell, xPosition, yPosition, { maxWidth: columnWidths[i] });
                xPosition += columnWidths[i];
            });

            yPosition += 7;
        });

        return doc;
    };

    const handleExportPDF = async () => {
        setExportingPdf(true);
        try {
            let expensesToExport = [];
            let dateRange = "";

            if (exportType === "monthly") {
                const now = new Date();
                const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

                expensesToExport = claims.filter(c => {
                    const claimDate = new Date(c.date);
                    return claimDate >= monthStart && claimDate <= monthEnd;
                });

                dateRange = `${monthStart.toLocaleDateString('en-IN')} to ${monthEnd.toLocaleDateString('en-IN')} (This Month)`;
            } else if (exportType === "yearly") {
                const now = new Date();
                const yearStart = new Date(now.getFullYear(), 0, 1);
                const yearEnd = new Date(now.getFullYear(), 11, 31);

                expensesToExport = claims.filter(c => {
                    const claimDate = new Date(c.date);
                    return claimDate >= yearStart && claimDate <= yearEnd;
                });

                dateRange = `${yearStart.toLocaleDateString('en-IN')} to ${yearEnd.toLocaleDateString('en-IN')} (This Year)`;
            } else if (exportType === "custom") {
                if (!customStartDate || !customEndDate) {
                    toast.error("Please select both start and end dates");
                    setExportingPdf(false);
                    return;
                }

                const startDate = new Date(customStartDate);
                const endDate = new Date(customEndDate);

                if (startDate > endDate) {
                    toast.error("Start date cannot be after end date");
                    setExportingPdf(false);
                    return;
                }

                expensesToExport = claims.filter(c => {
                    const claimDate = new Date(c.date);
                    return claimDate >= startDate && claimDate <= endDate;
                });

                dateRange = `${startDate.toLocaleDateString('en-IN')} to ${endDate.toLocaleDateString('en-IN')} (Custom)`;
            }

            if (expensesToExport.length === 0) {
                toast.error("No expenses found for the selected period");
                setExportingPdf(false);
                return;
            }

            const doc = generatePDF(expensesToExport, dateRange);
            const filename = `Expense_Report_${exportType}_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(filename);

            setShowExportModal(false);
            setExportType("monthly");
            setCustomStartDate("");
            setCustomEndDate("");
        } catch (err) {
            console.error("Failed to generate PDF:", err);
            toast.error("Failed to generate PDF");
        } finally {
            setExportingPdf(false);
        }
    };

    // Calculate all titles for filtering
    const allTitles = [...new Set(claims.map(c => c.title))].sort();

    const isSelectedMonth = (date) => {
        const d = new Date(date);
        return d.getMonth() === selectedMonth.getMonth() && d.getFullYear() === selectedMonth.getFullYear();
    };

    // Calculate analytics - for ALL claims (not just filtered)
    const approvedClaims = claims.filter(c => c.status === "Approved");
    const approvedThisMonth = approvedClaims.filter(c => isSelectedMonth(c.date));
    const totalApprovedAmount = approvedClaims.reduce((sum, c) => sum + c.amount, 0);
    const totalApprovedThisMonth = approvedThisMonth.reduce((sum, c) => sum + c.amount, 0);

    // Team-wise expense breakdown
    const expensesByTeam = {};
    approvedClaims.forEach(c => {
        const team = c.employee?.team || "Unknown";
        expensesByTeam[team] = (expensesByTeam[team] || 0) + c.amount;
    });

    // Title-wise expense breakdown
    const expensesByTitle = {};
    approvedClaims.forEach(c => {
        expensesByTitle[c.title] = (expensesByTitle[c.title] || 0) + c.amount;
    });

    // Filter Claims
    const filteredClaims = claims.filter(c => {
        // Status filter
        if (c.status !== statusTab) return false;

        // Team filter
        if (teamFilter !== "all" && c.employee?.team?.toLowerCase() !== teamFilter.toLowerCase()) return false;

        // Title filter
        if (titleFilter !== "all" && c.title !== titleFilter) return false;

        // Search query (name, email, ID, claim title)
        if (searchQuery.trim() !== "") {
            const query = searchQuery.toLowerCase();
            const name = c.employee?.name?.toLowerCase() || "";
            const email = c.employee?.email?.toLowerCase() || "";
            const empId = c.employee?.employeeId?.toLowerCase() || "";
            const title = c.title?.toLowerCase() || "";
            return name.includes(query) || email.includes(query) || empId.includes(query) || title.includes(query);
        }

        return true;
    });

    const totalPages = Math.ceil(filteredClaims.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentClaims = filteredClaims.slice(indexOfFirstItem, indexOfLastItem);

    const pendingCount = claims.filter(c => c.status === "Pending").length;
    const approvedCount = claims.filter(c => c.status === "Approved").length;
    const rejectedCount = claims.filter(c => c.status === "Rejected").length;

    if (loading) {
        return (
            <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center">
                Loading claims...
            </div>
        );
    }

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="flex-1 p-6 md:p-8 space-y-6 overflow-hidden">
                
                {/* HERO */}
                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] shadow-[var(--shadow-soft)] rounded-3xl p-8">
                    <div className="flex items-center justify-between gap-6">
                        <div>
                            <h1 className="text-4xl font-extrabold tracking-tight">Expense Claims</h1>
                            <p className="mt-3 text-lg text-[var(--text-secondary)]">Review and manage expense claims submitted by team members</p>
                        </div>
                        <div className="flex flex-col gap-3 items-end">
                            <button
                                onClick={() => setShowExportModal(true)}
                                className="flex items-center gap-2 bg-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/90 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg"
                            >
                                <Download size={18} />
                                Export Report
                            </button>
                            <div className="hidden md:flex w-20 h-20 rounded-3xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] items-center justify-center">
                                <IndianRupee size={34} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ANALYTICS DASHBOARD */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Total Approved Expenses */}
                    <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-soft)]">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Total Approved</p>
                                <p className="text-2xl font-bold text-emerald-500 mt-2">₹{totalApprovedAmount.toLocaleString('en-IN')}</p>
                                <p className="text-xs text-[var(--text-secondary)] mt-1">{approvedClaims.length} claims</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                                <CheckCircle2 size={24} />
                            </div>
                        </div>
                    </div>

                    {/* This Month's Approved Expenses - with Calendar */}
                    <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-soft)] cursor-pointer hover:border-[var(--accent-primary)] transition-colors relative"
                        onClick={() => setShowMonthCalendar(!showMonthCalendar)}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Selected Month</p>
                                <p className="text-2xl font-bold text-[var(--accent-primary)] mt-2">₹{totalApprovedThisMonth.toLocaleString('en-IN')}</p>
                                <p className="text-xs text-[var(--text-secondary)] mt-1">{approvedThisMonth.length} claims</p>
                                <p className="text-xs text-[var(--accent-primary)] font-semibold mt-2">
                                    {selectedMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                                </p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center">
                                <Calendar size={24} />
                            </div>
                        </div>

                        {/* Calendar Popup */}
                        {showMonthCalendar && (
                            <div className="absolute top-full left-0 mt-2 z-40 bg-[var(--card-bg)] dark:bg-[#121214] border border-[var(--border-color)] rounded-2xl p-4 shadow-xl">
                                <div className="flex items-center justify-between mb-4">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1));
                                        }}
                                        className="p-1 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                    <span className="font-bold text-sm">
                                        {selectedMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                                    </span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1));
                                        }}
                                        className="p-1 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                                <div className="text-xs text-center text-[var(--text-secondary)]">
                                    Click outside to close
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Close calendar when clicking outside */}
                    {showMonthCalendar && (
                        <div className="fixed inset-0 z-30" onClick={() => setShowMonthCalendar(false)} />
                    )}

                    {/* Average Expense */}
                    <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-soft)]">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Avg Expense</p>
                                <p className="text-2xl font-bold text-sky-500 mt-2">₹{approvedClaims.length > 0 ? Math.round(totalApprovedAmount / approvedClaims.length).toLocaleString('en-IN') : "0"}</p>
                                <p className="text-xs text-[var(--text-secondary)] mt-1">Per claim</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-sky-500/10 text-sky-500 flex items-center justify-center">
                                <TrendingUp size={24} />
                            </div>
                        </div>
                    </div>

                    {/* Total Claims All Status */}
                    <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-soft)]">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Total Claims</p>
                                <p className="text-2xl font-bold text-purple-500 mt-2">{claims.length}</p>
                                <p className="text-xs text-[var(--text-secondary)] mt-1">All statuses</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-[var(--accent-subtle)] text-[var(--accent-primary)] flex items-center justify-center">
                                <FileText size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* BREAKDOWN SECTIONS */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Expenses by Team */}
                    <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-soft)]">
                        <div className="flex items-center gap-2 mb-4">
                            <Briefcase size={20} className="text-[var(--accent-primary)]" />
                            <h3 className="font-bold text-lg">Expenses by Team</h3>
                        </div>
                        <div className="space-y-3">
                            {Object.entries(expensesByTeam).length > 0 ? (
                                Object.entries(expensesByTeam)
                                    .sort(([, a], [, b]) => b - a)
                                    .map(([team, amount]) => (
                                        <div key={team} className="flex items-center justify-between bg-[var(--bg-secondary)] p-3 rounded-xl">
                                            <span className="text-sm font-medium text-[var(--text-secondary)]">{team}</span>
                                            <span className="font-bold text-[var(--accent-primary)]">₹{amount.toLocaleString('en-IN')}</span>
                                        </div>
                                    ))
                            ) : (
                                <p className="text-sm text-[var(--text-secondary)] italic">No approved expenses</p>
                            )}
                        </div>
                    </div>

                    {/* Expenses by Title */}
                    <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-soft)]">
                        <div className="flex items-center gap-2 mb-4">
                            <Tag size={20} className="text-[var(--accent-primary)]" />
                            <h3 className="font-bold text-lg">Expenses by Category</h3>
                        </div>
                        <div className="space-y-3">
                            {Object.entries(expensesByTitle).length > 0 ? (
                                Object.entries(expensesByTitle)
                                    .sort(([, a], [, b]) => b - a)
                                    .slice(0, 5)
                                    .map(([title, amount]) => (
                                        <div key={title} className="flex items-center justify-between bg-[var(--bg-secondary)] p-3 rounded-xl">
                                            <span className="text-sm font-medium text-[var(--text-secondary)] truncate">{title}</span>
                                            <span className="font-bold text-[var(--accent-primary)] ml-2">₹{amount.toLocaleString('en-IN')}</span>
                                        </div>
                                    ))
                            ) : (
                                <p className="text-sm text-[var(--text-secondary)] italic">No approved expenses</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* FILTERS & SEARCH */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-3.5 text-neutral-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search employee or claim title..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-[var(--card-bg)] border border-[var(--border-color)] pl-12 pr-4 py-3 rounded-2xl outline-none focus:border-[var(--accent-primary)] transition-all font-medium text-sm text-[var(--text-primary)]"
                        />
                    </div>
                    <select
                        value={teamFilter}
                        onChange={(e) => setTeamFilter(e.target.value)}
                        className="bg-[var(--card-bg)] border border-[var(--border-color)] px-4 py-3 rounded-2xl outline-none focus:border-[var(--accent-primary)] transition-all font-medium text-sm text-[var(--text-primary)]"
                    >
                        <option value="all">All Departments / Teams</option>
                        {teams.map(t => (
                            <option key={t._id} value={t.value}>{t.name}</option>
                        ))}
                    </select>

                    <select
                        value={titleFilter}
                        onChange={(e) => setTitleFilter(e.target.value)}
                        className="bg-[var(--card-bg)] border border-[var(--border-color)] px-4 py-3 rounded-2xl outline-none focus:border-[var(--accent-primary)] transition-all font-medium text-sm text-[var(--text-primary)]"
                    >
                        <option value="all">All Expense Types</option>
                        {allTitles.map(title => (
                            <option key={title} value={title}>{title}</option>
                        ))}
                    </select>
                </div>

                {/* STATUS CARDS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Pending Card */}
                    <button
                        onClick={() => setStatusTab("Pending")}
                        className={`bg-[var(--card-bg)] border rounded-2xl p-6 shadow-[var(--shadow-soft)] transition-all ${statusTab === "Pending" ? "border-[var(--accent-primary)] ring-2 ring-[var(--accent-primary)]/20" : "border-[var(--border-color)] hover:border-[var(--accent-primary)]"}`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="text-left">
                                <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Pending</p>
                                <p className="text-4xl font-bold text-[var(--accent-primary)] mt-3">{pendingCount}</p>
                                <p className="text-xs text-[var(--text-secondary)] mt-2">Claims awaiting approval</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center flex-shrink-0">
                                <Clock size={24} />
                            </div>
                        </div>
                    </button>

                    {/* Approved Card */}
                    <button
                        onClick={() => setStatusTab("Approved")}
                        className={`bg-[var(--card-bg)] border rounded-2xl p-6 shadow-[var(--shadow-soft)] transition-all ${statusTab === "Approved" ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-[var(--border-color)] hover:border-emerald-500"}`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="text-left">
                                <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Approved</p>
                                <p className="text-4xl font-bold text-emerald-500 mt-3">{approvedCount}</p>
                                <p className="text-xs text-[var(--text-secondary)] mt-2">Claims approved for payment</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center flex-shrink-0">
                                <CheckCircle2 size={24} />
                            </div>
                        </div>
                    </button>

                    {/* Rejected Card */}
                    <button
                        onClick={() => setStatusTab("Rejected")}
                        className={`bg-[var(--card-bg)] border rounded-2xl p-6 shadow-[var(--shadow-soft)] transition-all ${statusTab === "Rejected" ? "border-red-500 ring-2 ring-red-500/20" : "border-[var(--border-color)] hover:border-red-500"}`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="text-left">
                                <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Rejected</p>
                                <p className="text-4xl font-bold text-red-500 mt-3">{rejectedCount}</p>
                                <p className="text-xs text-[var(--text-secondary)] mt-2">Claims rejected with reasons</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center flex-shrink-0">
                                <XCircle size={24} />
                            </div>
                        </div>
                    </button>
                </div>

                {/* DATA TABLE */}
                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-3xl overflow-hidden shadow-[var(--shadow-soft)]">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-[var(--border-color)] bg-neutral-100 dark:bg-neutral-900/50">
                                    <th className="p-5 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Employee</th>
                                    <th className="p-5 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Expense Details</th>
                                    <th className="p-5 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Date</th>
                                    <th className="p-5 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Description</th>
                                    <th className="p-5 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider text-center">Receipt</th>
                                    {statusTab !== "Pending" && (
                                        <th className="p-5 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Processed Details</th>
                                    )}
                                    {statusTab === "Pending" && (
                                        <th className="p-5 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider text-right">Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--border-color)]">
                                {filteredClaims.length === 0 ? (
                                    <tr>
                                        <td colSpan={statusTab === "Pending" ? 6 : 7} className="p-12 text-center text-neutral-400">
                                            <AlertCircle size={40} className="mx-auto mb-3 opacity-20 text-[var(--text-secondary)]" />
                                            <p className="font-semibold text-lg">No expense claims found</p>
                                        </td>
                                    </tr>
                                ) : (
                                    currentClaims.map(c => (
                                        <tr key={c._id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900/25 transition-colors">
                                            <td className="p-5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center font-bold">
                                                        {c.employee?.name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-[var(--text-primary)] text-sm">{c.employee?.name}</h4>
                                                        <p className="text-xs text-[var(--text-secondary)] mt-0.5 capitalize">{c.employee?.team} • ID: {c.employee?.employeeId}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-5">
                                                <h4 className="font-bold text-sm text-[var(--text-primary)]">{c.title}</h4>
                                                <p className="text-sm font-bold text-[var(--accent-primary)] mt-1">₹{c.amount.toLocaleString('en-IN')}</p>
                                            </td>
                                            <td className="p-5 text-sm font-medium text-neutral-500 dark:text-neutral-400">
                                                {formatDate(c.date)}
                                            </td>
                                            <td className="p-5 text-sm text-neutral-500 dark:text-neutral-400 max-w-xs">
                                                {c.description || <span className="italic text-neutral-600">No notes provided</span>}
                                            </td>
                                            <td className="p-5 text-center">
                                                {c.receiptName ? (
                                                    <button
                                                        onClick={() => handleViewReceipt(c._id)}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[var(--border-color)] hover:border-[var(--accent-primary)] text-xs rounded-xl font-bold transition-all text-neutral-700 dark:text-neutral-300 bg-[var(--bg-secondary)]"
                                                    >
                                                        <Eye size={12} />
                                                        View PDF
                                                    </button>
                                                ) : (
                                                    <span className="text-xs italic text-neutral-600">No receipt</span>
                                                )}
                                            </td>
                                            {statusTab !== "Pending" && (
                                                <td className="p-5 text-sm">
                                                    <div className="space-y-1.5">
                                                        <div className="flex items-center gap-1 text-xs text-neutral-500 font-medium">
                                                            <span className="font-semibold text-neutral-600 dark:text-neutral-300">Processed at:</span>
                                                            <span>{c.processedAt ? formatDate(c.processedAt) : "N/A"}</span>
                                                        </div>
                                                        {c.processedBy && (
                                                            <div className="flex items-center gap-1 text-xs text-neutral-500 font-semibold">
                                                                <span className="font-semibold text-neutral-600 dark:text-neutral-300">By:</span>
                                                                <span className="text-neutral-700 dark:text-neutral-200">{c.processedBy.name}</span>
                                                                <span className="text-neutral-400">({formatRoleLabel(c.processedBy.role) || "Admin"})</span>
                                                            </div>
                                                        )}
                                                        {c.rejectionReason && (
                                                            <p className="text-xs text-red-400 font-bold max-w-xs bg-red-500/5 p-2 rounded-lg border border-red-500/10">Rejection Reason: {c.rejectionReason}</p>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            {statusTab === "Pending" && (
                                                <td className="p-5 text-right">
                                                    <div className="inline-flex gap-2">
                                                        <button
                                                            onClick={() => handleApprove(c._id)}
                                                            disabled={processingId !== ""}
                                                            className="w-9 h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex items-center justify-center transition-all shadow-md active:scale-95 disabled:opacity-50"
                                                            title="Approve Claim"
                                                        >
                                                            <Check size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => openRejectModal(c)}
                                                            disabled={processingId !== ""}
                                                            className="w-9 h-9 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center justify-center transition-all shadow-md active:scale-95 disabled:opacity-50"
                                                            title="Reject Claim"
                                                        >
                                                            <X size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between bg-[var(--card-bg)] border border-[var(--border-color)] px-6 py-4 rounded-3xl shadow-[var(--shadow-soft)] mt-4">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredClaims.length)} of {filteredClaims.length} entries
                        </span>
                        <div className="flex gap-2">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                className="px-4 py-2 bg-[var(--bg-secondary)] hover:bg-[var(--border-color)] text-sm font-bold rounded-xl transition-all disabled:opacity-40"
                            >
                                Previous
                            </button>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                className="px-4 py-2 bg-[var(--bg-secondary)] hover:bg-[var(--border-color)] text-sm font-bold rounded-xl transition-all disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}

                {/* REJECTION MODAL */}
                {rejectingClaim && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setRejectingClaim(null)} />
                        
                        <div className="relative w-full max-w-md bg-[var(--card-bg)] dark:bg-[#121214] border border-neutral-200 dark:border-neutral-800 rounded-3xl shadow-2xl p-6 z-10 animate-fade-in">
                            <h3 className="text-lg font-bold mb-2">Reject Expense Claim</h3>
                            <p className="text-xs text-neutral-400 mb-4">Please provide a brief explanation for rejecting <strong>{rejectingClaim.employee?.name}</strong>'s claim (₹{rejectingClaim.amount.toLocaleString('en-IN')}).</p>
                            
                            <form onSubmit={handleRejectSubmit} className="space-y-4">
                                <textarea
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Enter reason for rejection..."
                                    required
                                    rows={4}
                                    className="w-full bg-[var(--bg-secondary)] border border-[var(--border-color)] p-4 rounded-2xl outline-none focus:border-[var(--accent-primary)] resize-none text-sm font-medium"
                                />
                                <div className="flex gap-3 justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setRejectingClaim(null)}
                                        className="px-5 py-2.5 rounded-xl bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold transition-colors text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submittingReject}
                                        className="px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors text-sm disabled:opacity-50"
                                    >
                                        {submittingReject ? "Rejecting..." : "Confirm Reject"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* EXPORT PDF MODAL */}
                {showExportModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowExportModal(false)} />
                        
                        <div className="relative w-full max-w-md bg-[var(--card-bg)] dark:bg-[#121214] border border-neutral-200 dark:border-neutral-800 rounded-3xl shadow-2xl p-6 z-10">
                            <h3 className="text-lg font-bold mb-2">Export Expense Report</h3>
                            <p className="text-xs text-neutral-400 mb-4">Select the period for which you want to generate a PDF report</p>
                            
                            <form className="space-y-4">
                                {/* Export Type Selection */}
                                <div className="space-y-3">
                                    <label className="flex items-center gap-3 p-3 border border-neutral-200 dark:border-neutral-800 rounded-xl cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors">
                                        <input 
                                            type="radio" 
                                            name="exportType" 
                                            value="monthly"
                                            checked={exportType === "monthly"}
                                            onChange={(e) => setExportType(e.target.value)}
                                            className="w-4 h-4"
                                        />
                                        <div>
                                            <p className="font-medium text-sm">This Month</p>
                                            <p className="text-xs text-neutral-400">Export current month's expenses</p>
                                        </div>
                                    </label>

                                    <label className="flex items-center gap-3 p-3 border border-neutral-200 dark:border-neutral-800 rounded-xl cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors">
                                        <input 
                                            type="radio" 
                                            name="exportType" 
                                            value="yearly"
                                            checked={exportType === "yearly"}
                                            onChange={(e) => setExportType(e.target.value)}
                                            className="w-4 h-4"
                                        />
                                        <div>
                                            <p className="font-medium text-sm">This Year</p>
                                            <p className="text-xs text-neutral-400">Export current year's expenses</p>
                                        </div>
                                    </label>

                                    <label className="flex items-center gap-3 p-3 border border-neutral-200 dark:border-neutral-800 rounded-xl cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors">
                                        <input 
                                            type="radio" 
                                            name="exportType" 
                                            value="custom"
                                            checked={exportType === "custom"}
                                            onChange={(e) => setExportType(e.target.value)}
                                            className="w-4 h-4"
                                        />
                                        <div>
                                            <p className="font-medium text-sm">Custom Range</p>
                                            <p className="text-xs text-neutral-400">Export for a specific date range</p>
                                        </div>
                                    </label>
                                </div>

                                {/* Custom Date Range */}
                                {exportType === "custom" && (
                                    <div className="space-y-3 p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-xl">
                                        <div>
                                            <label className="block text-xs font-medium mb-1.5">Start Date</label>
                                            <input 
                                                type="date"
                                                value={customStartDate}
                                                onChange={(e) => setCustomStartDate(e.target.value)}
                                                className="w-full bg-[var(--card-bg)] dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-800 p-2 rounded-lg outline-none focus:border-[var(--accent-primary)] text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1.5">End Date</label>
                                            <input 
                                                type="date"
                                                value={customEndDate}
                                                onChange={(e) => setCustomEndDate(e.target.value)}
                                                className="w-full bg-[var(--card-bg)] dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-800 p-2 rounded-lg outline-none focus:border-[var(--accent-primary)] text-sm"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-3 justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setShowExportModal(false)}
                                        disabled={exportingPdf}
                                        className="px-5 py-2.5 rounded-xl bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold transition-colors text-sm disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleExportPDF}
                                        disabled={exportingPdf}
                                        className="px-6 py-2.5 rounded-xl bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] shadow-sm text-white font-semibold transition-colors text-sm disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <Download size={16} />
                                        {exportingPdf ? "Generating..." : "Export PDF"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Expenses;


